import type { ReploElementType, StoreProduct } from "schemas/generated/element";
import type { RuntimeContextNullableValueMap } from ".";

import {
  DEFAULT_ACTIVE_SHOPIFY_URL_ROOT,
  DEFAULT_MONEY_FORMAT,
  DEFAULT_MONEY_WITH_CURRENCY_FORMAT,
} from "replo-runtime/shared/liquid";
import { componentInventory } from "replo-runtime/store/componentInventory";
import { fakeProducts } from "replo-runtime/store/utils/fakeProducts";

/**
 * This can be used to create a default context map for testing and for setting
 * up a runtime context outside of a traditional page.
 */
export const createRuntimeContextStub = (config: {
  element: {
    type: ReploElementType;
    projectId: string;
  };
  shopifyProducts?: StoreProduct[];
}): RuntimeContextNullableValueMap => {
  const { element } = config;
  return {
    componentError: null,
    componentInventory: { componentInventory },
    componentUpdate: null,
    customFonts: { uploadedFonts: [] },
    draftElement: null,
    designLibrary: null,
    dynamicDataStore: { store: {} },
    extraContext: {
      disableLiquid: false,
      okendoNamespace: "okendoReviews",
      runtimeVersion: null,
    },
    featureFlags: {
      featureFlags: {
        carouselDebug: false,
      },
    },
    renderEnvironment: {
      renderEnvironment: "publishedPage",
      isEditorApp: false,
      isPublishing: false,
      previewEnvironment: null,
      isElementPreview: false,
      isPublishedPage: true,
    },
    // biome-ignore lint/style/noRestrictedGlobals: allow global
    globalWindow: window,
    editorCanvas: "desktop",
    reploEditorCanvas: null,
    reploEditorActiveCanvas: null,
    reploElement: {
      elementType: element.type,
      useSectionSettings: false,
    },
    reploSymbols: { symbols: [] },
    syncRuntimeState: null,
    shopifyStore: {
      templateProduct: null,
      storeId: element.projectId,
      storeUrl: "alchemy-faketown.myshopify.com",
      fakeProducts: fakeProducts,
      activeCurrency: "USD",
      activeLanguage: new Intl.Locale("en-us").language,
      moneyFormat: DEFAULT_MONEY_FORMAT,
      moneyWithCurrencyFormat: DEFAULT_MONEY_WITH_CURRENCY_FORMAT,
      activeShopifyUrlRoot: DEFAULT_ACTIVE_SHOPIFY_URL_ROOT,
    },
    runtimeHooks: {
      usePreviewWidth() {
        return 1000;
      },
      useEditorOverrideActiveVariantId(_componentId) {
        return null;
      },
      useEditorOverrideTextValue(_componentId) {
        return null;
      },
      useIsEditorEditModeRenderEnvironment() {
        return false;
      },
      useIsLabelledByOtherComponent() {
        return false;
      },
      useSwatches() {
        return [];
      },
      useSharedState() {
        return null;
      },
      useSetSharedState() {
        return () => {
          return null;
        };
      },
      useTemplateEditorProduct() {
        return null;
      },
      useShopifyProducts() {
        return config.shopifyProducts ?? [];
      },
      useIsShopifyProductsLoading() {
        return false;
      },
      useShopifyProductMetafieldValues() {
        return {};
      },
      useShopifyVariantMetafieldValues() {
        return {};
      },
      useIsContentEditing() {
        return false;
      },
      useIsUploadingEditorMediaId() {
        return false;
      },
      useIsBrokenEditorMediaId() {
        return false;
      },
      useSetEditorBrokenMediaComponentId() {
        return () => {
          return null;
        };
      },

      useEditorOverrideOpenModalId() {
        return null;
      },
      useDesignLibrary() {
        return null;
      },
    },
  };
};
